import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from './pictures/frame-logo.png';
import CustomTypography from './CustomTypography';
import MailchimpForm from './NewsLetter';
import { useFetchSections, getImagePath } from './Actions/FetchContent';

// ─────────────────────────────────────────────────────────────────────────────

// ── Social icons skeleton — exactly 24px tall, matches real icons ─────────────
const SocialSkeleton = () => (
  <div className="flex flex-row gap-3" style={{ minHeight: 24 }}>
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} style={{ width: 24, height: 24, borderRadius: '50%', background: '#d1d5db', flexShrink: 0 }} />
    ))}
  </div>
);

// ── Privacy links skeleton — 1.5rem tall, matches real p3 links ──────────────
const PrivacySkeleton = () => (
  <>
    {[0, 1, 2].map(i => (
      <div key={i} style={{ height: '1.5rem', width: '80px', background: '#e5e7eb', borderRadius: 4 }} />
    ))}
  </>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const sections = "76,77,78,79,80";
  const { data } = useFetchSections(sections);
  const [microValues,   setMicroValues]   = useState([]);
  const [newsValues,    setNewsValues]    = useState([]);
  const [involveValues, setInvolveValues] = useState([]);
  const [policyValues,  setPolicyValues]  = useState([]);
  const [iconValues,    setIconValues]    = useState([]);
  const [dataReady,     setDataReady]     = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      setMicroValues(data.filter(item => item.secid === 76));
      setNewsValues(data.filter(item => item.secid === 77));
      setInvolveValues(data.filter(item => item.secid === 78));
      setPolicyValues(data.filter(item => item.secid === 79));
      setIconValues(data.filter(item => item.secid === 80));
      setDataReady(true);
    } else {
      console.error('No data found for the given sections.');
    }
  }, [data]);

  const renderLink = (program, index) => {
    const link = program.answer
      ? program.answer.replace(/<[^>]+>/g, '').trim()
      : '';

    const isExternal = link?.startsWith('http');
    const label = (
      <CustomTypography as='p' variant='p3'
        color="text-customBlack hover:text-customGreen"
        weightFont='font-normal hover:font-bold' alignment='text-left'
        html={program.question} />
    );
    return isExternal
      ? <a key={index} href={link} target="_blank" rel="noopener noreferrer">{label}</a>
      : <Link key={index} to={link} onClick={() => window.scrollTo(0, 0)}>{label}</Link>;
  };

  const microFinance  = microValues.map(renderLink);
  const NewsLinks     = newsValues.map(renderLink);
  const involvedLinks = involveValues.map(renderLink);

  const privacyLinks = policyValues.length > 1
    ? policyValues.slice(1).map((program, index) => {
        const link = program.answer
          ? program.answer.replace(/<[^>]+>/g, '').trim()
          : '';
        const isExternal = link?.startsWith('http');
        const label = (
          <CustomTypography as='p' variant='p3'
            color="text-customBlack hover:text-customGreen"
            weightFont='font-normal hover:font-bold' alignment='text-center'
            html={program.question} />
        );
        return isExternal
          ? <a key={index} href={link} target="_blank" rel="noopener noreferrer">{label}</a>
          : <Link key={index} to={link} onClick={() => window.scrollTo(0, 0)}>{label}</Link>;
      })
    : [];

  // Social icons — skeleton while loading, real icons after dataReady
  const socialLinks = dataReady
    ? iconValues.map((program, index) => (
        <a key={index} href={program.link} target="_blank" rel="noopener noreferrer">
          <img
            src={getImagePath(program.image_url)}
            alt="social icon"
            width="24"
            height="24"
            style={{ width: '24px', height: '24px', display: 'block', flexShrink: 0 }}
            loading="lazy"
            decoding="async"
          />
        </a>
      ))
    : null;

  return (
    // ── Plain <footer> replaces MUI <Box> ────────────────────────────────────
    // "footer-root" class is defined in footer.css (imported in App.js).
    // It carries the background-image rules for the SVG patterns.
    // DO NOT move those rules back into a JS-injected <style> tag.
    <footer className="footer-root bg-primaryLight lg:pt-60px lg:pb-10 lg:px-10 md:pt-10 md:pb-90px pt-10 pb-10 px-4"
      style={{ minHeight: 320 }}>
      <div className='max-w-screen-2xl mx-auto'>

        {/* ── Top row: logo column + mailchimp ─────────────────────────────── */}
        <div className="flex lg:flex-row md:flex-row flex-col justify-between gap-3"
          style={{ minHeight: 180 }}>

          <div className='flex flex-col justify-between'>
            <div className='flex justify-start items-center'>
              <img
                src={logo}
                alt="footer logo"
                className='w-[80px]'
                width="80"
                height="80"
                style={{ width: '80px', height: '80px', display: 'block', flexShrink: 0 }}
                loading="lazy"
                decoding="async"
              />
            </div>

            {/* Social icons: skeleton shown while iconValues API loads.
                Both skeleton and real icons are 24px tall → no height change. */}
            <div className='flex flex-row gap-3 justify-start lg:pt-0 pt-5' style={{ minHeight: 24 }}>
              {socialLinks === null ? <SocialSkeleton /> : socialLinks}
            </div>
          </div>

          <div style={{ minHeight: 180, flexShrink: 0 }}>
            <MailchimpForm />
          </div>

        </div>

        {/* ── Commented nav columns — uncomment to restore ─────────────────── */}
        
        <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-5 pt-6'>

          <div className="pt-4 border-t-2 border-t-topGreen flex flex-col gap-4">
            <CustomTypography as='p' variant='p3' className="font-manrope text-topGreen py-4 font-bold text-[14px]
                                    lg:leading-[14px] lg:text-left md:text-left text-center"> Microfinance </CustomTypography>
            {microFinance}
          </div>

          <div className="pt-4 border-t-2 border-t-topGreen flex flex-col gap-4">
            <CustomTypography as='p' variant='p3' className="font-manrope text-topGreen py-4 font-bold text-[14px]
                                    lg:leading-[14px] lg:text-left md:text-left text-center"> News & Resources </CustomTypography>
            {NewsLinks}
          </div>

          <div className="pt-4 border-t-2 border-t-topGreen flex flex-col gap-4">
            <CustomTypography as='p' variant='p3' className="font-manrope text-topGreen py-4 font-bold text-[14px]
                                    lg:leading-[14px] lg:text-left md:text-left text-center"> Get Involved </CustomTypography>
            {involvedLinks}
          </div>

          <div className="pt-4 border-t-2 border-t-topGreen flex flex-col gap-5 lg:hidden">
            {privacyLinks}
          </div>

        </div>
        

        <div className="pt-6 border-b border-topGreen"></div>

        {/* ── Bottom bar ───────────────────────────────────────────────────── */}
        <div className='flex lg:flex-row md:flex-row flex-col grid-cols-1 gap-5 justify-between pt-5'
          style={{ minHeight: '2rem' }}>

          <div style={{ minHeight: '1.5rem' }}>
            <a href={policyValues[0]?.answer || '/'} style={{ display: 'block', minHeight: '1.5rem' }}>
              <CustomTypography as='p' variant='p3'
                color="text-customBlack hover:text-customGreen"
                weightFont='font-normal hover:font-bold'
                alignment='lg:text-center md:text-center text-left'>
                {policyValues[0]?.question || '\u00A0'}
              </CustomTypography>
            </a>
          </div>

          {/* PrivacySkeleton: 3 grey bars same height as real links.
              Swapped out for real links when policyValues arrives. */}
          <div className='lg:grid lg:grid-cols-3 hidden gap-3 lg:justify-start'
            style={{ minHeight: '1.5rem' }}>
            {privacyLinks.length > 0 ? privacyLinks : <PrivacySkeleton />}
          </div>

          {/* Copyright is static — no API dependency, no risk */}
          <div style={{ minHeight: '1.5rem' }}>
            <CustomTypography as='p' variant='p3'
              color="text-customBlack hover:text-customGreen"
              weightFont='font-normal hover:font-bold'
              alignment='lg:text-center md:text-center text-left'>
              © {currentYear} Akhuwat. All rights reserved.
            </CustomTypography>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;